import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CreditCard, CheckCircle, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { PLAN_CONFIG } from "../../config/tripPlans";

export function PaymentWall({ trip }) {
  const [selectedPlan, setSelectedPlan] = useState("confort");
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { t } = useLanguage();

  const handlePayment = async () => {
    if (!termsAccepted) {
      toast.error(t("payment.terms_required"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { trip_id: trip.id, origin_url: window.location.origin, plan_type: selectedPlan }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("payment.error"));
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-12 px-5">
      <div className="text-center mb-10 animate-fade-in-up">
        <p className="section-label mb-3">{t("payment.ready_label")}</p>
        <h2
          className="text-4xl mb-3"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
        >
          {t("payment.validated_title")}
        </h2>
        <p className="text-sm text-[#5C554E] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {t("payment.validated_desc", { dest: trip.destination })}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mb-8" data-testid="payment-wall">
        {Object.entries(PLAN_CONFIG).map(([key, plan]) => {
          const selected = selectedPlan === key;
          return (
            <div
              key={key}
              onClick={() => setSelectedPlan(key)}
              className={`relative flex flex-col cursor-pointer transition-all ${
                selected ? "border-2 border-[#C17F59] shadow-md" : "border border-[#DCD4C6] hover:border-[#8B5E34]"
              }`}
              style={{ background: selected ? "#FDF8F3" : "#F9F6F0", borderRadius: "2px" }}
              data-testid={`plan-option-${key}`}
            >
              {plan.popular && (
                <div
                  className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#C17F59] text-[#F9F6F0] text-xs px-4 py-1 whitespace-nowrap"
                  style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px" }}
                >
                  {t("payment.popular")}
                </div>
              )}
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
                      {plan.name}
                    </h3>
                    <p className="text-xs text-[#9A8F86] italic mt-0.5" style={{ fontFamily: "'Lora', Georgia, serif" }}>{plan.desc}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-1 flex items-center justify-center ${selected ? "border-[#C17F59] bg-[#C17F59]" : "border-[#DCD4C6]"}`}>
                    {selected && <div className="w-2 h-2 bg-[#F9F6F0] rounded-full" />}
                  </div>
                </div>
                <div className="mb-4">
                  <span className="text-4xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#C17F59" }}>
                    {plan.price} €
                  </span>
                  <span className="text-xs text-[#9A8F86] ml-1.5 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>{t("payment.per_trip")}</span>
                </div>
                <ul className="space-y-2 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                      <CheckCircle size={12} className="text-[#C17F59] mt-0.5 flex-shrink-0" strokeWidth={2} />{f}
                    </li>
                  ))}
                  {plan.excluded.map((f, i) => (
                    <li key={`ex-${i}`} className="flex items-start gap-2 text-xs text-[#C8C0B8]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                      <X size={12} className="text-[#C8C0B8] mt-0.5 flex-shrink-0" strokeWidth={2} />{f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div className="max-w-sm mx-auto text-center">
        {/* Terms checkbox */}
        <label
          className="flex items-start gap-3 text-left mb-5 cursor-pointer group"
          data-testid="terms-checkbox-label"
        >
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="sr-only"
              data-testid="terms-checkbox"
            />
            <div
              className={`w-5 h-5 border-2 flex items-center justify-center transition-all ${
                termsAccepted
                  ? "border-[#C17F59] bg-[#C17F59]"
                  : "border-[#DCD4C6] bg-[#F9F6F0] group-hover:border-[#8B5E34]"
              }`}
              style={{ borderRadius: "2px" }}
            >
              {termsAccepted && (
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="#F9F6F0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>
          <p className="text-xs text-[#5C554E] leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            {t("payment.terms_checkbox_pre")}{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C17F59] hover:underline font-medium"
              data-testid="terms-link"
              onClick={(e) => e.stopPropagation()}
            >
              {t("payment.terms_link")}
            </Link>
            {" "}{t("payment.terms_checkbox_post")}
          </p>
        </label>

        <button
          onClick={handlePayment}
          disabled={loading || !termsAccepted}
          className={`btn-primary w-full flex items-center justify-center gap-2 mb-3 transition-all ${
            !termsAccepted ? "opacity-40 cursor-not-allowed" : ""
          }`}
          data-testid="payment-btn"
        >
          <CreditCard size={15} strokeWidth={1.5} />
          {loading
            ? t("payment.unlocking")
            : t("payment.unlock_btn", { name: PLAN_CONFIG[selectedPlan]?.name, price: PLAN_CONFIG[selectedPlan]?.price })}
        </button>
        <p className="text-xs text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {t("payment.secure")}
        </p>
      </div>
    </div>
  );
}
